import { zodResolver } from '@hookform/resolvers/zod';
import { IAccountRespSchema } from '@monetize/types/app';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { useUpdateAccountHierarchy } from '../../../api/accountsService';
import { AccountSelect } from '../../../components/Account/AccountSelect';
import { MBox, MButton, MFormField } from '../../../components/Monetize';

const FormSchema = z.object({ parentId: z.string().nullable() });
type Form = z.infer<typeof FormSchema>;

interface AccountHierarchyFormProps {
  account: IAccountRespSchema;
  isReadOnly?: boolean;
  onSave: () => void;
}

export const AccountHierarchyForm = ({
  account,
  isReadOnly = false,
  onSave,
}: AccountHierarchyFormProps) => {
  const { mutateAsync, isPending } = useUpdateAccountHierarchy();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      parentId: account.parentId || null,
    },
  });

  const onSubmit = async (data: Form) => {
    await mutateAsync({
      accountId: account.id,
      parentAccountId: data.parentId,
    });
    onSave();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <MBox w="100%">
        <MFormField
          label="Parent Account"
          error={errors.parentId}
          tooltip="Changing the parent account will update the account hierarchy accordingly."
        >
          <Controller
            name="parentId"
            control={control}
            render={({ field }) => (
              <AccountSelect
                {...field}
                isDisabled={isReadOnly || isPending}
                isReadOnly={isReadOnly}
                variant={isReadOnly ? 'readonly' : 'outline'}
                returnItem={false}
                disabledIds={[account.id]}
              />
            )}
          />
        </MFormField>
      </MBox>
      <MBox mt={4}>
        <MButton
          variant="primary"
          type="submit"
          isDisabled={!isValid || !isDirty || isPending}
          isLoading={isPending}
        >
          Save
        </MButton>
      </MBox>
    </form>
  );
};
